import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArtEngine, OUTSIDE, type StrokePoint } from './engine';
import { EffectPlayer, liftOff, pour, puff, tapRing } from './effects';
import { medium, paintColor, type BrushKind } from './media';
import { Feedback } from '@/services/feedback';
import { cn } from '@/design-system/cn';

export type Tool = 'fill' | 'brush' | 'erase';

interface Props {
  engine: ArtEngine;
  tool: Tool;
  brush: BrushKind;
  /** Paint spec — a colour, possibly with a finish (see media.ts). */
  color: string;
  brushSize: number;
  zoom: number;
  /** Notified on every committed operation so the editor can autosave. */
  onOperation: () => void;
  /** Quieter presentation: bedtime, or the system's reduced-motion setting. */
  calm?: boolean;
  /**
   * Keep each mark inside whatever it started in. A small hand cannot hold a
   * line; this makes that not matter, which is most of why colouring on a
   * screen can feel better than colouring on paper.
   */
  stayInside: boolean;
  className?: string;
  /** Accessible name for the drawing surface. */
  label: string;
}

/**
 * The drawing surface (spec §9.2).
 *
 * Input rules implemented here:
 *  - Pointer capture, so a stroke that leaves the canvas still completes.
 *  - Multi-touch is treated as a gesture, not a stroke: a second finger
 *    cancels the in-flight stroke and rolls back its marks, so pinching to
 *    zoom never leaves an accidental line.
 *  - `touch-action: none` stops the page scrolling under the child's hand.
 *  - Fill is an explicit tool. A short tap with the brush selected draws a
 *    dot; it never guesses a flood fill.
 *  - Orientation and size changes re-fit the canvas without losing the art,
 *    because the document lives in art coordinates, not screen pixels.
 *
 * There are three stacked canvases, in this order:
 *  1. the artwork, painted from the engine;
 *  2. the effects layer, which plays lift-off and pour animations and is
 *     never part of the document;
 *  3. nothing else — the pointer stays on the artwork canvas so hit testing
 *     and pointer capture have a single owner.
 */
export function StudioCanvas({
  engine,
  tool,
  brush,
  color,
  brushSize,
  zoom,
  onOperation,
  calm,
  stayInside,
  className,
  label,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fxRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState({ w: 0, h: 0 });
  const [drawing, setDrawing] = useState(false);
  const liveRef = useRef<{
    pointerId: number;
    points: StrokePoint[];
    cancelled: boolean;
    seed: number;
    /** Fixed at pointer-down: where this mark is allowed to go. */
    clip: string | null;
  } | null>(null);
  const rafRef = useRef<number>(0);
  const activePointers = useRef(new Set<number>());

  const player = useMemo(() => new EffectPlayer(engine.width, engine.height), [engine]);
  useEffect(() => {
    player.attach(fxRef.current);
    return () => player.destroy();
  }, [player]);

  // The system setting wins over anything the app would like to do.
  const reduced = useReducedMotion();
  const quiet = calm || reduced;

  /**
   * Brush size in art units.
   *
   * Scaled by the medium (a spray is wider than a pencil) AND by the size of
   * the document, so "Medium" looks the same on a small template and on a large
   * blank sheet. Without the second term the same pen draws a fat line on one
   * page and a hairline on another.
   */
  const artSize = brushSize * medium(brush).scale * (engine.width / 1000);
  const inkColor = paintColor(color);

  /** Repaints the visible canvas from the engine. */
  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    const cssW = canvas.clientWidth;
    const cssH = canvas.clientHeight;
    if (cssW === 0 || cssH === 0) return;

    const targetW = Math.round(cssW * dpr);
    const targetH = Math.round(cssH * dpr);
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
    }

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Art space -> device pixels. One transform, applied once.
    const scale = (cssW / engine.width) * dpr;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    // The stroke in progress is composited INSIDE the brush layer rather than
    // painted over the finished picture, so it sits under the line art exactly
    // where it will end up — no jump when the finger lifts. It uses the SAME
    // seed the committed stroke will use, so the grain does not reshuffle
    // either. Nothing about it enters the document or the history.
    const live = liveRef.current;
    const preview =
      live && !live.cancelled && live.points.length > 0 && tool !== 'fill'
        ? {
            points: live.points,
            style: { color: inkColor, size: artSize, kind: brush, seed: live.seed },
            erase: tool === 'erase',
            clip: live.clip,
          }
        : null;
    engine.composite(ctx, { live: preview });
  }, [engine, tool, inkColor, artSize, brush]);

  const schedulePaint = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      paint();
    });
  }, [paint]);

  useLayoutEffect(() => {
    engine.onChange = schedulePaint;
    schedulePaint();
    return () => {
      engine.onChange = undefined;
    };
  }, [engine, schedulePaint]);

  /**
   * Fit the document inside the available box on BOTH axes. Templates have
   * different aspect ratios (spec §9.1), so a width-only rule clips a tall
   * picture and a height-only rule clips a wide one.
   */
  const measure = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const availW = el.clientWidth;
    const availH = el.clientHeight;
    if (availW === 0 || availH === 0) return;
    const scale = Math.min(availW / engine.width, availH / engine.height) * zoom;
    setFit({
      w: Math.max(1, Math.floor(engine.width * scale)),
      h: Math.max(1, Math.floor(engine.height * scale)),
    });
  }, [engine.width, engine.height, zoom]);

  // Re-fit on resize AND orientation change. The artwork is unaffected —
  // only the presentation transform changes (spec §9.1).
  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      measure();
      schedulePaint();
    });
    ro.observe(el);
    const onOrient = () => {
      measure();
      schedulePaint();
    };
    window.addEventListener('orientationchange', onOrient);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', onOrient);
    };
  }, [measure, schedulePaint]);

  useEffect(() => {
    schedulePaint();
  }, [fit, schedulePaint]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  /** Screen point -> art coordinates, accounting for the displayed bounds. */
  const toArt = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>): StrokePoint => {
      const canvas = canvasRef.current!;
      const rect = canvas.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * engine.width;
      const y = ((e.clientY - rect.top) / rect.height) * engine.height;
      // Only trust pressure when the device actually reports a pen.
      const p = e.pointerType === 'pen' && e.pressure > 0 ? e.pressure : 0.5;
      return { x, y, p };
    },
    [engine.width, engine.height],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    activePointers.current.add(e.pointerId);

    // Second finger = gesture. Abandon any in-flight stroke cleanly.
    if (activePointers.current.size > 1) {
      if (liveRef.current) {
        liveRef.current.cancelled = true;
        liveRef.current = null;
        setDrawing(false);
        schedulePaint();
      }
      return;
    }

    const pt = toArt(e);

    if (tool === 'fill') {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      // isPointInPath needs an untransformed context to test art coordinates.
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      const regionId = engine.regionAt(pt.x, pt.y, ctx);
      ctx.restore();
      if (regionId) {
        const before = engine.regionColors[regionId] ?? null;
        engine.fill(regionId, color);
        if (!quiet && before !== color) {
          const path = engine.pathFor(regionId);
          // The wipe has to be able to clear the whole region from wherever
          // the child tapped, so reach for the far corner.
          const reach = Math.hypot(
            Math.max(pt.x, engine.width - pt.x),
            Math.max(pt.y, engine.height - pt.y),
          );
          if (path) player.play(pour(path, before, paintColor(color), pt.x, pt.y, reach));
        }
        if (!quiet) player.play(tapRing(pt.x, pt.y, inkColor, engine.width * 0.05));
        Feedback.fill();
        onOperation();
      }
      return;
    }

    // Which shape this mark belongs to is decided ONCE, at the moment the
    // finger lands, and never re-evaluated — so a stroke that wanders is held
    // where it started rather than jumping between shapes mid-line.
    let clip: string | null = null;
    if (stayInside && tool !== 'erase') {
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx) {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        clip = engine.regionAt(pt.x, pt.y, ctx) ?? OUTSIDE;
        ctx.restore();
      }
    }

    e.currentTarget.setPointerCapture(e.pointerId);
    liveRef.current = {
      pointerId: e.pointerId,
      points: [pt],
      cancelled: false,
      // Seeded from the moment of the stroke — stable for its whole life.
      seed: (Math.random() * 0xffffffff) >>> 0,
      clip,
    };
    setDrawing(true);
    schedulePaint();
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const live = liveRef.current;
    if (!live || live.pointerId !== e.pointerId || live.cancelled) return;
    if (activePointers.current.size > 1) return;

    // Coalesced events keep fast strokes smooth without extra sampling cost.
    const native = e.nativeEvent as PointerEvent & { getCoalescedEvents?: () => PointerEvent[] };
    const events = native.getCoalescedEvents?.() ?? [];
    if (events.length > 1) {
      const canvas = canvasRef.current!;
      const rect = canvas.getBoundingClientRect();
      for (const ce of events) {
        live.points.push({
          x: ((ce.clientX - rect.left) / rect.width) * engine.width,
          y: ((ce.clientY - rect.top) / rect.height) * engine.height,
          p: ce.pointerType === 'pen' && ce.pressure > 0 ? ce.pressure : 0.5,
        });
      }
    } else {
      live.points.push(toArt(e));
    }
    schedulePaint();
  };

  const endStroke = (e: React.PointerEvent<HTMLCanvasElement>, cancelled: boolean) => {
    activePointers.current.delete(e.pointerId);
    const live = liveRef.current;
    if (!live || live.pointerId !== e.pointerId) return;
    liveRef.current = null;
    setDrawing(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    if (cancelled || live.cancelled || live.points.length === 0) {
      schedulePaint();
      return;
    }
    engine.commitStroke(
      live.points,
      { color: inkColor, size: artSize, kind: brush, seed: live.seed },
      tool === 'erase',
      live.clip,
    );

    // The lift-off. It plays AFTER the mark is committed, so the flourish is
    // never mistaken for the mark — if it were interrupted, nothing is lost.
    if (!quiet) {
      const last = live.points[live.points.length - 1];
      player.play(
        tool === 'erase'
          ? puff(last.x, last.y, artSize, live.seed)
          : liftOff(live.points, brush, inkColor, artSize, live.seed),
      );
    }
    onOperation();
  };

  return (
    <div
      ref={wrapRef}
      className={cn('relative flex h-full w-full items-center justify-center overflow-hidden', className)}
    >
      <div
        className="relative transition-transform duration-200 ease-out"
        style={{
          width: fit.w ? `${fit.w}px` : '100%',
          height: fit.h ? `${fit.h}px` : 'auto',
          aspectRatio: `${engine.width} / ${engine.height}`,
          // The paper dips very slightly under the hand. Small enough to feel
          // rather than notice.
          transform: drawing && !quiet ? 'scale(0.994)' : 'scale(1)',
        }}
      >
        <canvas
          ref={canvasRef}
          aria-label={label}
          role="img"
          data-testid="studio-canvas"
          className={cn(
            'absolute inset-0 h-full w-full rounded-pebble bg-white ring-2 ring-line transition-shadow duration-200',
            drawing && !quiet ? 'shadow-press' : 'shadow-cut',
          )}
          style={{
            touchAction: 'none',
            cursor: tool === 'fill' ? 'crosshair' : 'default',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={(e) => endStroke(e, false)}
          onPointerCancel={(e) => endStroke(e, true)}
          onPointerLeave={(e) => {
            // Only ends the stroke if capture was somehow lost.
            if (!e.currentTarget.hasPointerCapture?.(e.pointerId)) endStroke(e, false);
          }}
          onContextMenu={(e) => e.preventDefault()}
        />
        <canvas
          ref={fxRef}
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full rounded-pebble"
        />
      </div>
    </div>
  );
}

/** Honours the operating system's reduced-motion setting, live. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const on = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduced;
}
